import { useState, useEffect } from "react";
import { Plus, X, Upload, Image as ImageIcon, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const CREATE_CATEGORY_VALUE = "__create__";

export function DishEditor({ initial, categories, productNames, productPriceMap, subRecipeDishes = [], baseCurrency, saving, onCancel, onSubmit, lang, onCategoriesChanged }) {
  // Assign a stable client-side _uid to every ingredient so React's key
  // stays stable across add/remove.
  const seedIngs = (arr) => (arr || []).map((it) => (
    it._uid ? it : { ...it, _uid: crypto.randomUUID() }
  ));
  const [draft, setDraft] = useState(() => ({
    // Sensible defaults for the two new planning fields.
    merma_pct: initial.merma_pct ?? 10,
    target_margin_pct: initial.target_margin_pct ?? null,
    ...initial,
    ingredients: seedIngs(initial.ingredients),
  }));
  const computeGross = (net, vat) => {
    const n = parseFloat(net);
    const v = parseFloat(vat);
    if (isNaN(n) || isNaN(v)) return "";
    return (n * (1 + v / 100)).toFixed(2);
  };
  const [grossDraft, setGrossDraft] = useState(() => computeGross(initial.sale_price, initial.pvp_vat_pct ?? 10));
  const [grossFocused, setGrossFocused] = useState(false);
  const [createCatOpen, setCreateCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatLabel, setNewCatLabel] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);

  const handleCategoryChange = (val) => {
    if (val === CREATE_CATEGORY_VALUE) {
      setNewCatName("");
      setNewCatLabel("");
      setCreateCatOpen(true);
      return;
    }
    setField("category", val);
  };

  const submitNewCategory = async () => {
    const name = (newCatName || "").trim().toLowerCase();
    if (!name) {
      toast.error(lang === "es" ? "Nombre requerido" : "Name required");
      return;
    }
    setCreatingCat(true);
    try {
      const { data } = await api.post("/dish-categories", {
        name,
        label: (newCatLabel || newCatName).trim() || name,
      });
      toast.success(lang === "es" ? "Categoría creada" : "Category created");
      setField("category", data.name);
      setCreateCatOpen(false);
      onCategoriesChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingCat(false);
    }
  };

  // Resync the gross display when net or vat changes externally (and the gross field is not being edited)
  useEffect(() => {
    if (!grossFocused) {
      setGrossDraft(computeGross(draft.sale_price, draft.pvp_vat_pct ?? 10));
    }
  }, [draft.sale_price, draft.pvp_vat_pct, grossFocused]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setIng = (idx, k, v) => setDraft((d) => ({
    ...d,
    ingredients: d.ingredients.map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
  }));
  const addIng = () => setDraft((d) => ({
    ...d,
    ingredients: [...d.ingredients, { _uid: crypto.randomUUID(), product_name: "", qty: "", unit: "", unit_price_override: "", vat_pct: "10" }],
  }));
  const removeIng = (idx) => setDraft((d) => ({
    ...d,
    ingredients: d.ingredients.filter((_, i) => i !== idx),
  }));

  // iter 82.7o — sub-recipe picker. Adds another dish (salsa/guarnición) as
  // an ingredient with its cost-per-portion locked in as unit_price_override
  // so the parent dish inherits the sub-recipe cost without re-computing.
  const [subPickerOpen, setSubPickerOpen] = useState(false);
  // iter 82.7n — when a sub-recipe candidate has a sale_price > 0 we ask the
  // user whether to lock the cost at "coste real" or at "PVP/ración".
  const [subChoice, setSubChoice] = useState(null); // {dish, costVal, pvpVal}

  const openSubCandidate = (dish) => {
    const costVal = Number(dish?.cost_per_portion_with_merma ?? dish?.cost_per_portion ?? 0) || 0;
    const servings = Number(dish?.servings) || 1;
    const salePrice = Number(dish?.sale_price) || 0;
    const pvpVal = salePrice > 0 ? salePrice / servings : 0;
    // If the sub-recipe has no PVP we skip the chooser and lock the real cost.
    if (pvpVal <= 0) {
      addSubRecipe(dish, "cost", costVal);
      return;
    }
    setSubChoice({ dish, costVal, pvpVal });
  };

  const addSubRecipe = (dish, mode, unitPrice) => {
    const price = Number(unitPrice) || 0;
    setDraft((d) => ({
      ...d,
      ingredients: [
        ...d.ingredients,
        {
          _uid: crypto.randomUUID(),
          product_name: (lang === "es" ? "[Sub-receta] " : "[Sub-recipe] ") + dish.name,
          qty: "1",
          unit: lang === "es" ? "porción" : "portion",
          unit_price_override: String(price.toFixed(4).replace(/\.?0+$/, "")),
          vat_pct: "10",
          sub_dish_id: dish.id,
          price_mode: mode, // "cost" | "pvp"
        },
      ],
    }));
    setSubPickerOpen(false);
    setSubChoice(null);
  };

  // iter 82.7n — photo upload. We POST multipart to `/dishes/{id}/photo`
  // *only* once the dish has an id (saved). Un-saved drafts can't upload.
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoRefreshKey, setPhotoRefreshKey] = useState(0);
  const hasPhoto = !!(initial.id && initial.photo_url);
  const photoSrc = hasPhoto ? `${API}${initial.photo_url}?k=${photoRefreshKey}` : null;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!initial.id) {
      toast.info(lang === "es"
        ? "Guarda el plato primero para poder añadir la foto."
        : "Save the dish first to add a photo.");
      e.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(lang === "es" ? "Máximo 2 MB" : "Max 2 MB");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/dishes/${initial.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(lang === "es" ? "Foto actualizada" : "Photo updated");
      initial.photo_url = `/api/dishes/${initial.id}/photo`;
      setPhotoRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const handlePhotoDelete = async () => {
    if (!initial.id) return;
    if (!window.confirm(lang === "es" ? "¿Eliminar la foto del plato?" : "Delete the dish photo?")) return;
    try {
      await api.delete(`/dishes/${initial.id}/photo`);
      toast.success(lang === "es" ? "Foto eliminada" : "Photo deleted");
      initial.photo_url = null;
      setPhotoRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmtPrice = (v) => {
    if (v == null) return "—";
    try {
      return new Intl.NumberFormat("es-ES", { style: "currency", currency: baseCurrency, maximumFractionDigits: 4, useGrouping: "always" }).format(v);
    } catch {
      return `${v} ${baseCurrency}`;
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dish-edit-dialog">
        <DialogHeader>
          <DialogTitle>
            {initial.id
              ? (lang === "es" ? "Editar plato" : "Edit dish")
              : (lang === "es" ? "Nuevo plato" : "New dish")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1 col-span-2">
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "Nombre del plato" : "Dish name"}
              </Label>
              <Input
                value={draft.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder={lang === "es" ? "Ej. Ensalada César" : "e.g. Caesar salad"}
                data-testid="dish-name"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                name="dish-name-field"
                type="text"
                inputMode="text"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "Categoría" : "Category"}
              </Label>
              <Select value={draft.category || ""} onValueChange={handleCategoryChange}>
                <SelectTrigger className="h-9" data-testid="dish-category">
                  <SelectValue placeholder={lang === "es" ? "Selecciona una categoría" : "Pick a category"} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.name} value={c.name} data-testid={`dish-category-opt-${c.name}`}>
                      <span className="capitalize">{c.label || c.name}</span>
                    </SelectItem>
                  ))}
                  <SelectItem value={CREATE_CATEGORY_VALUE} data-testid="dish-category-create-opt">
                    <span className="inline-flex items-center gap-1 text-[var(--primary)]">
                      <Plus size={12} />
                      {lang === "es" ? "Crear nueva categoría…" : "Create new category…"}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "PVP por ración (neto ↔ con IVA)" : "PVP per portion (net ↔ gross)"}
              </Label>
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-5">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={draft.sale_price}
                    onChange={(e) => setField("sale_price", e.target.value)}
                    className="font-mono-num h-9"
                    placeholder={lang === "es" ? "sin IVA" : "net"}
                    data-testid="dish-sale-price"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    {lang === "es" ? "PVP sin IVA" : "PVP net"}
                  </p>
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={draft.pvp_vat_pct ?? "10"}
                    onChange={(e) => setField("pvp_vat_pct", e.target.value)}
                    className="font-mono-num h-9"
                    data-testid="dish-pvp-vat"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">IVA %</p>
                </div>
                <div className="col-span-5">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={grossDraft}
                    onFocus={() => setGrossFocused(true)}
                    onBlur={() => {
                      setGrossFocused(false);
                      // Normalize to recomputed gross from net so values stay consistent on close
                      const normalized = computeGross(draft.sale_price, draft.pvp_vat_pct ?? 10);
                      if (normalized !== "") setGrossDraft(normalized);
                    }}
                    onChange={(e) => {
                      // Accept any string while typing (incl. trailing "." or ",")
                      const raw = e.target.value.replace(",", ".");
                      setGrossDraft(raw);
                      const gross = parseFloat(raw);
                      const vat = parseFloat(draft.pvp_vat_pct);
                      if (isNaN(gross) || isNaN(vat)) return;
                      const net = gross / (1 + vat / 100);
                      // Store with higher precision so the round-trip back to gross preserves user's value
                      setField("sale_price", net.toFixed(4));
                    }}
                    className="font-mono-num h-9 border-[var(--primary)]/40"
                    placeholder={lang === "es" ? "con IVA" : "gross"}
                    data-testid="dish-sale-price-gross"
                    autoComplete="off"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    {lang === "es" ? "PVP con IVA" : "PVP gross"}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "Raciones" : "Servings"}
              </Label>
              <Input
                type="number"
                step="1"
                min="1"
                value={draft.servings}
                onChange={(e) => setField("servings", e.target.value)}
                className="font-mono-num"
                data-testid="dish-servings"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setField("active", e.target.checked)}
                data-testid="dish-active"
                className="rounded border-[var(--border-color)]"
              />
              {lang === "es" ? "Activo en la carta" : "On the menu"}
            </label>

            {/* Merma (waste %) + target margin — planning fields. */}
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "Merma %" : "Waste %"}
              </Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={draft.merma_pct ?? "10"}
                onChange={(e) => setField("merma_pct", e.target.value)}
                className="font-mono-num h-9"
                data-testid="dish-merma-pct"
                placeholder="10"
              />
              <p className="text-[10px] text-[var(--text-muted)]">
                {lang === "es"
                  ? "Aplica sobre el coste de ingredientes. Por defecto 10%."
                  : "Applied over ingredient cost. Default 10%."}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "Margen objetivo %" : "Target margin %"}
              </Label>
              <Input
                type="number"
                step="1"
                min="0"
                max="99"
                value={draft.target_margin_pct ?? ""}
                onChange={(e) => setField("target_margin_pct", e.target.value === "" ? null : e.target.value)}
                className="font-mono-num h-9"
                data-testid="dish-target-margin-pct"
                placeholder={lang === "es" ? "opcional" : "optional"}
              />
              <p className="text-[10px] text-[var(--text-muted)]">
                {lang === "es"
                  ? "Sugiere un PVP para alcanzar este margen sobre el coste con merma."
                  : "Suggests a PVP to hit this margin over the cost with waste."}
              </p>
            </div>
          </div>

          {/* Fecha de última actualización (sólo al editar) */}
          {initial.id && initial.updated_at && (
            <div className="text-[11px] text-[var(--text-muted)] -mt-1" data-testid="dish-updated-at">
              {lang === "es" ? "Última actualización" : "Last updated"}: {new Date(initial.updated_at).toLocaleString(lang === "es" ? "es-ES" : "en-US", {
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit",
              })}
            </div>
          )}

          {/* iter 82.7z — banner global cuando hay ingredientes con precio nuevo */}
          {(() => {
            const changedCount = (draft.ingredients || []).filter(
              (ing) => ing.price_changed === true && ing.new_billed_price != null
            ).length;
            if (changedCount === 0) return null;
            return (
              <div
                className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-2"
                data-testid="dish-price-drift-banner"
              >
                <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <p className="font-bold text-amber-700 dark:text-amber-300">
                    {lang === "es"
                      ? `${changedCount} ingrediente(s) con precio nuevo en factura`
                      : `${changedCount} ingredient(s) have a new invoice price`}
                  </p>
                  <p className="text-xs mt-0.5 text-[var(--text-primary)]">
                    {lang === "es"
                      ? "El escandallo mantiene los precios snapshot. Pulsa \"Actualizar\" en cada ingrediente para aceptar el precio nuevo."
                      : "The recipe keeps the snapshotted prices. Click \"Apply\" per ingredient to accept the new price."}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* iter 82.7n — AVISO GRANDE si food cost >40% o <10% */}
          {initial.id && (initial.outlier_high || initial.outlier_low) && (
            <div
              data-testid="dish-outlier-banner"
              className={`rounded-lg border-2 p-3 flex items-start gap-2 ${
                initial.outlier_high
                  ? "border-[var(--status-negative)]/70 bg-[var(--status-negative-bg)]"
                  : "border-[var(--status-warning)]/70 bg-[var(--status-warning-bg)]"
              }`}
            >
              <AlertTriangle size={22} className={initial.outlier_high ? "text-[var(--status-negative)]" : "text-[var(--status-warning)]"} />
              <div className="flex-1 text-sm">
                <p className="font-bold">
                  {initial.outlier_high
                    ? (lang === "es"
                        ? `⚠ FOOD COST ALTO: ${(initial.food_cost_pct ?? 0).toFixed(1)}% (> 40%)`
                        : `⚠ HIGH FOOD COST: ${(initial.food_cost_pct ?? 0).toFixed(1)}% (> 40%)`)
                    : (lang === "es"
                        ? `⚠ FOOD COST BAJO: ${(initial.food_cost_pct ?? 0).toFixed(1)}% (< 10%)`
                        : `⚠ LOW FOOD COST: ${(initial.food_cost_pct ?? 0).toFixed(1)}% (< 10%)`)}
                </p>
                <p className="text-xs mt-0.5 text-[var(--text-primary)]">
                  {initial.outlier_high
                    ? (lang === "es"
                        ? "Este plato cuesta más del 40%. Revisa precios de ingredientes, merma o sube el PVP."
                        : "This dish costs over 40%. Review ingredient prices, waste, or raise the PVP.")
                    : (lang === "es"
                        ? "Este plato parece casi gratis. Suele indicar precios de ingredientes incompletos."
                        : "This dish is almost free. Usually means missing ingredient prices.")}
                </p>
              </div>
            </div>
          )}

          {/* Coste con merma + PVP sugerido (informativo). */}
          <CostSummary draft={draft} baseCurrency={baseCurrency} fmtPrice={fmtPrice} lang={lang} />

          {/* iter 82.7n — Foto del plato (solo tras guardar) */}
          <div
            className="rounded-lg border border-[var(--border-color)] bg-[var(--surface-hover)]/40 p-3 flex items-center gap-3"
            data-testid="dish-photo-section"
          >
            {photoSrc ? (
              <img
                src={photoSrc}
                alt={initial.name || "dish"}
                className="w-24 h-24 rounded object-cover border border-[var(--border-color)]"
                data-testid="dish-photo-preview"
              />
            ) : (
              <div className="w-24 h-24 rounded bg-[var(--surface)] border border-dashed border-[var(--border-color)] flex items-center justify-center text-[var(--text-muted)]">
                <ImageIcon size={28} />
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-1">
              <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Foto del plato" : "Dish photo"}
              </p>
              <p className="text-xs text-[var(--text-secondary)]">
                {initial.id
                  ? (lang === "es" ? "Máx 2 MB · JPG/PNG. Aparece en la lista y el PDF." : "Max 2 MB · JPG/PNG. Shown in list & PDF.")
                  : (lang === "es" ? "Guarda el plato primero para subir la foto." : "Save the dish first to upload a photo.")}
              </p>
              <div className="flex items-center gap-2 pt-1">
                <label className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer ${
                  initial.id ? "border-[var(--primary)]/40 hover:bg-[var(--primary)]/10" : "opacity-50 cursor-not-allowed"
                }`}>
                  <Upload size={12} />
                  {uploadingPhoto
                    ? (lang === "es" ? "Subiendo…" : "Uploading…")
                    : hasPhoto
                      ? (lang === "es" ? "Cambiar" : "Change")
                      : (lang === "es" ? "Subir foto" : "Upload photo")}
                  <input
                    type="file"
                    accept="image/*"
                    disabled={!initial.id || uploadingPhoto}
                    onChange={handlePhotoUpload}
                    className="hidden"
                    data-testid="dish-photo-input"
                  />
                </label>
                {hasPhoto && (
                  <button
                    type="button"
                    onClick={handlePhotoDelete}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[var(--status-negative)]/40 text-[var(--status-negative)] hover:bg-[var(--status-negative)]/10"
                    data-testid="dish-photo-delete"
                  >
                    <Trash2 size={12} />
                    {lang === "es" ? "Quitar" : "Remove"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Ingredients */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "Ingredientes" : "Ingredients"}
              </Label>
              <div className="flex items-center gap-2">
                {subRecipeDishes.length > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setSubPickerOpen((v) => !v)}
                    data-testid="dish-add-sub-recipe"
                    title={lang === "es" ? "Añadir salsa o guarnición ya creada" : "Add existing sauce or side"}
                  >
                    <Plus size={12} className="mr-1" />
                    {lang === "es" ? "Sub-receta" : "Sub-recipe"}
                  </Button>
                )}
                <Button type="button" size="sm" variant="outline" onClick={addIng} data-testid="dish-add-ingredient">
                  <Plus size={12} className="mr-1" />
                  {lang === "es" ? "Añadir" : "Add"}
                </Button>
              </div>
            </div>
            {subPickerOpen && subRecipeDishes.length > 0 && (
              <div
                className="border border-[var(--brand-accent)]/40 bg-[var(--brand-accent)]/5 rounded-md p-2 max-h-52 overflow-y-auto"
                data-testid="dish-sub-recipe-picker"
              >
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1.5 px-1">
                  {lang === "es"
                    ? "Elige una salsa o guarnición existente — se añadirá como ingrediente con su coste por porción bloqueado."
                    : "Pick an existing sauce or side — added as ingredient with its per-portion cost locked in."}
                </p>
                <div className="space-y-1">
                  {subRecipeDishes.map((sd) => {
                    const cost = Number(sd.cost_per_portion_with_merma ?? sd.cost_per_portion ?? 0);
                    return (
                      <button
                        type="button"
                        key={sd.id}
                        onClick={() => openSubCandidate(sd)}
                        data-testid={`dish-sub-recipe-pick-${sd.id}`}
                        className="w-full text-left flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-[var(--surface-hover)] text-xs"
                      >
                        <span className="truncate">
                          <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mr-1.5">
                            {sd.category}
                          </span>
                          {sd.name}
                        </span>
                        <span className="font-mono-num text-[var(--brand-accent)]">
                          {fmtPrice(cost)} / {lang === "es" ? "porción" : "portion"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-[10px] text-[var(--text-muted)]">
              {lang === "es"
                ? "Verás el precio automático tomado de la última factura. Si no es correcto, escribe el precio manualmente en la columna \"Precio €/ud\" para sobrescribirlo."
                : "You'll see the auto price taken from the latest invoice. If it's wrong, type a manual price in the \"Price €/unit\" column to override."}
            </p>
            <datalist id="product-options">
              {productNames.map((n) => <option key={n} value={n} />)}
            </datalist>
            <div className="space-y-1.5">
              {draft.ingredients.length === 0 && (
                <p className="text-xs text-[var(--text-muted)] py-3 text-center border border-dashed border-[var(--border-color)] rounded">
                  {lang === "es" ? "Sin ingredientes todavía." : "No ingredients yet."}
                </p>
              )}
              {draft.ingredients.length > 0 && (
                <div className="grid grid-cols-12 gap-2 px-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                  <div className="col-span-4">{lang === "es" ? "Producto" : "Product"}</div>
                  <div className="col-span-2">{lang === "es" ? "Cant." : "Qty"}</div>
                  <div className="col-span-2">{lang === "es" ? "Unidad" : "Unit"}</div>
                  <div className="col-span-3">
                    {lang === "es" ? "Precio €/ud" : "Price €/unit"}
                    <span className="ml-1 normal-case tracking-normal text-[9px] opacity-70">
                      {lang === "es" ? "(s/IVA · vacío = auto)" : "(net · empty = auto)"}
                    </span>
                  </div>
                  <div className="col-span-1" />
                </div>
              )}
              {draft.ingredients.map((ing, idx) => {
                const nameKey = (ing.product_name || "").trim().toLowerCase();
                const autoPrice = nameKey ? productPriceMap[nameKey] : undefined;
                const hasOverride = ing.unit_price_override !== "" && ing.unit_price_override != null;
                const effectivePrice = hasOverride
                  ? parseFloat(ing.unit_price_override) || 0
                  : (autoPrice != null ? autoPrice : 0);
                const qtyNum = parseFloat(ing.qty) || 0;
                const lineCost = qtyNum * effectivePrice;            // sin IVA
                const noAuto = nameKey && autoPrice == null;
                // iter 82.7z — drift detection: snapshot override differs
                // from the latest billed price. Prefer backend-provided flag
                // (`price_changed` + `new_billed_price`) because it already
                // excludes sub-recipes; fall back to a client calc for new
                // rows that haven't been saved yet.
                const backendChanged = ing.price_changed === true && ing.new_billed_price != null;
                const newBilled = backendChanged
                  ? Number(ing.new_billed_price)
                  : (hasOverride && autoPrice != null && Math.abs(parseFloat(ing.unit_price_override) - autoPrice) > 0.005 && !ing.sub_dish_id)
                    ? Number(autoPrice)
                    : null;
                const priceChanged = newBilled != null;
                return (
                  <div key={ing._uid || idx} data-testid={`dish-ing-row-${idx}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        list="product-options"
                        value={ing.product_name}
                        onChange={(e) => setIng(idx, "product_name", e.target.value.toLowerCase())}
                        placeholder={lang === "es" ? "Producto" : "Product"}
                        className="col-span-4 h-9"
                        data-testid={`dish-ing-name-${idx}`}
                      />
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={ing.qty}
                        onChange={(e) => setIng(idx, "qty", e.target.value)}
                        placeholder=""
                        className="col-span-2 h-9 font-mono-num"
                        data-testid={`dish-ing-qty-${idx}`}
                      />
                      <Input
                        value={ing.unit || ""}
                        onChange={(e) => setIng(idx, "unit", e.target.value)}
                        placeholder={lang === "es" ? "kg / L / ud" : "kg / L / unit"}
                        className="col-span-2 h-9"
                        data-testid={`dish-ing-unit-${idx}`}
                      />
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={ing.unit_price_override ?? ""}
                        onChange={(e) => setIng(idx, "unit_price_override", e.target.value)}
                        placeholder={autoPrice != null ? `auto: ${autoPrice}` : (lang === "es" ? "sin auto" : "no auto")}
                        className={`col-span-3 h-9 font-mono-num ${hasOverride ? "border-[var(--primary)]" : ""}`}
                        data-testid={`dish-ing-price-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeIng(idx)}
                        className="col-span-1 p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--status-negative)]"
                        data-testid={`dish-ing-remove-${idx}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {nameKey && (
                      <div className="grid grid-cols-12 gap-2 px-1 mt-0.5 text-[10px] text-[var(--text-muted)]">
                        <div className="col-span-8 truncate">
                          {priceChanged ? (
                            <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium">
                              <AlertTriangle size={11} />
                              {lang === "es"
                                ? <>Precio nuevo en factura: <strong>{fmtPrice(newBilled)}</strong> (actual: {fmtPrice(parseFloat(ing.unit_price_override) || 0)}).</>
                                : <>New invoice price: <strong>{fmtPrice(newBilled)}</strong> (current: {fmtPrice(parseFloat(ing.unit_price_override) || 0)}).</>}
                              <button
                                type="button"
                                onClick={() => setIng(idx, "unit_price_override", String(newBilled))}
                                className="ml-1 px-2 py-0.5 rounded bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-semibold"
                                data-testid={`dish-ing-price-apply-${idx}`}
                                title={lang === "es" ? "Actualizar al precio nuevo" : "Apply new price"}
                              >
                                {lang === "es" ? "Actualizar" : "Apply"}
                              </button>
                            </span>
                          ) : hasOverride ? (
                            <span className="text-[var(--primary)] font-medium">
                              {lang === "es" ? "Precio manual" : "Manual price"}
                              {autoPrice != null && (
                                <span className="ml-1 opacity-70">
                                  · {lang === "es" ? "auto sería" : "auto would be"} {fmtPrice(autoPrice)}
                                </span>
                              )}
                            </span>
                          ) : noAuto ? (
                            <span className="text-amber-500">
                              {lang === "es"
                                ? "Sin precio automático. Súbelo desde facturas o escribe uno manual."
                                : "No auto price. Upload an invoice or type a manual price."}
                            </span>
                          ) : (
                            <span>
                              {lang === "es" ? "Auto" : "Auto"}: {fmtPrice(autoPrice)}
                              {ing.unit ? ` / ${ing.unit}` : ""}
                            </span>
                          )}
                        </div>
                        <div className="col-span-3 text-right font-mono-num">
                          {qtyNum > 0 && effectivePrice > 0 ? (
                            <span data-testid={`dish-ing-linecost-${idx}`}>
                              = {fmtPrice(lineCost)}
                            </span>
                          ) : ""}
                        </div>
                        <div className="col-span-1" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving} data-testid="dish-edit-cancel">
            {lang === "es" ? "Cancelar" : "Cancel"}
          </Button>
          <Button
            type="button"
            onClick={() => {
              // Strip the client-side _uid before sending to the backend.
              const clean = {
                ...draft,
                ingredients: (draft.ingredients || []).map(({ _uid: _drop, ...rest }) => rest),
              };
              onSubmit(clean);
            }}
            disabled={saving || !draft.name?.trim()}
            data-testid="dish-edit-save"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            {saving
              ? (lang === "es" ? "Guardando…" : "Saving…")
              : (lang === "es" ? "Guardar" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
      {createCatOpen && (
        <Dialog open onOpenChange={(o) => !o && !creatingCat && setCreateCatOpen(false)}>
          <DialogContent className="max-w-sm" data-testid="dish-category-create-dialog">
            <DialogHeader>
              <DialogTitle>{lang === "es" ? "Nueva categoría de plato" : "New dish category"}</DialogTitle>
              <DialogDescription>
                {lang === "es"
                  ? "Añade una nueva categoría a tu carta (p. ej. tapas, brunch)."
                  : "Add a new category to your menu (e.g. tapas, brunch)."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Nombre (interno)" : "Name (internal)"}</Label>
                <Input
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value.toLowerCase())}
                  placeholder={lang === "es" ? "ej. tapas" : "e.g. tapas"}
                  autoFocus
                  data-testid="dish-category-create-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Etiqueta visible" : "Display label"}</Label>
                <Input
                  value={newCatLabel}
                  onChange={(e) => setNewCatLabel(e.target.value)}
                  placeholder={lang === "es" ? "ej. Tapas" : "e.g. Tapas"}
                  data-testid="dish-category-create-label"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateCatOpen(false)}
                disabled={creatingCat}
                data-testid="dish-category-create-cancel"
              >
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                onClick={submitNewCategory}
                disabled={creatingCat || !newCatName.trim()}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                data-testid="dish-category-create-save"
              >
                {creatingCat ? "…" : (lang === "es" ? "Crear" : "Create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* iter 82.7n — sub-recipe price mode chooser */}
      {subChoice && (
        <Dialog open onOpenChange={(o) => { if (!o) setSubChoice(null); }}>
          <DialogContent className="max-w-md" data-testid="dish-sub-price-mode-dialog">
            <DialogHeader>
              <DialogTitle>
                {lang === "es" ? "¿Qué precio usamos?" : "Which price to use?"}
              </DialogTitle>
              <DialogDescription>
                {lang === "es"
                  ? `"${subChoice.dish.name}" tiene PVP. Elige qué valor cargar al plato padre.`
                  : `"${subChoice.dish.name}" has a PVP. Choose which value to lock in.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => addSubRecipe(subChoice.dish, "cost", subChoice.costVal)}
                data-testid="dish-sub-price-mode-cost"
                className="w-full text-left rounded-md border border-[var(--border-color)] hover:border-[var(--primary)] p-3 flex items-center justify-between gap-3"
              >
                <div>
                  <p className="text-sm font-semibold">
                    {lang === "es" ? "Coste real (con merma)" : "Real cost (with waste)"}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {lang === "es"
                      ? "Lo que te cuesta producir una ración de la sub-receta."
                      : "What a portion of the sub-recipe actually costs you."}
                  </p>
                </div>
                <span className="font-mono-num text-[var(--primary)] text-sm">
                  {fmtPrice(subChoice.costVal)}
                </span>
              </button>
              <button
                type="button"
                onClick={() => addSubRecipe(subChoice.dish, "pvp", subChoice.pvpVal)}
                data-testid="dish-sub-price-mode-pvp"
                className="w-full text-left rounded-md border border-[var(--border-color)] hover:border-[var(--primary)] p-3 flex items-center justify-between gap-3"
              >
                <div>
                  <p className="text-sm font-semibold">
                    {lang === "es" ? "PVP por ración" : "PVP per portion"}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {lang === "es"
                      ? "Precio de venta sin IVA ÷ raciones. Útil cuando la cocina 'compra' internamente la sub-receta."
                      : "Net sale price ÷ servings. Useful when the kitchen 'buys' the sub-recipe internally."}
                  </p>
                </div>
                <span className="font-mono-num text-[var(--primary)] text-sm">
                  {fmtPrice(subChoice.pvpVal)}
                </span>
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSubChoice(null)}>
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}


/**
 * Resumen de coste en vivo (dentro del editor). Calcula:
 *   - coste base = Σ (qty × precio efectivo por ingrediente)
 *   - coste con merma = coste base × (1 + merma%/100)
 *   - PVP sugerido para alcanzar el margen objetivo (si está definido)
 * Muestra todo con formato moneda + porcentaje comparado con el PVP actual.
 */
function CostSummary({ draft, fmtPrice, lang }) {
  const merma = parseFloat(draft.merma_pct ?? 10) || 0;
  const servings = Math.max(1, parseInt(draft.servings) || 1);
  const target = draft.target_margin_pct == null || draft.target_margin_pct === ""
    ? null
    : parseFloat(draft.target_margin_pct);

  // Precio efectivo por línea: manual override si existe, si no 0 (no lo
  // conocemos en el cliente para el precio auto — el backend hace ese cálculo).
  const baseCost = (draft.ingredients || []).reduce((acc, it) => {
    const qty = parseFloat(it.qty) || 0;
    const override = it.unit_price_override;
    const auto = parseFloat(it.auto_unit_price) || 0;
    const price = (override !== "" && override != null) ? parseFloat(override) : auto;
    if (isFinite(price) && qty > 0) return acc + qty * price;
    return acc;
  }, 0);
  const costWithMerma = baseCost * (1 + merma / 100);
  // iter 82.7p — PVP es POR RACIÓN. Para calcular margen hay que comparar
  // PVP vs coste POR RACIÓN (no coste total).
  const costPerPortion = costWithMerma / servings;
  const pvp = parseFloat(draft.sale_price) || 0;
  const currentMarginAbs = pvp - costPerPortion;
  const currentMarginPct = pvp > 0 ? (currentMarginAbs / pvp) * 100 : null;

  // PVP sugerido para hitear el target margin: sale × (1 - m/100) = cost_ración.
  const suggested = target != null && target < 100
    ? costPerPortion / (1 - target / 100)
    : null;

  return (
    <div
      className="rounded-md border border-[var(--border-color)] bg-[var(--surface-hover)]/40 p-3 text-xs space-y-1"
      data-testid="dish-cost-summary"
    >
      <div className="flex items-center justify-between">
        <span className="text-[var(--text-muted)]">
          {lang === "es" ? "Coste de ingredientes" : "Ingredient cost"}
        </span>
        <span className="font-mono-num" data-testid="dish-cost-base">{fmtPrice(baseCost)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[var(--text-muted)]">
          {lang === "es" ? `Coste con merma (${merma.toFixed(1)}%)` : `Cost with waste (${merma.toFixed(1)}%)`}
        </span>
        <span className="font-mono-num font-semibold" data-testid="dish-cost-with-merma">{fmtPrice(costWithMerma)}</span>
      </div>
      {servings > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-[var(--text-muted)]">
            {lang === "es" ? `Coste por ración (÷${servings})` : `Cost per portion (÷${servings})`}
          </span>
          <span className="font-mono-num" data-testid="dish-cost-per-portion">{fmtPrice(costPerPortion)}</span>
        </div>
      )}
      {pvp > 0 && (
        <div className="flex items-center justify-between border-t border-[var(--border-color)]/60 pt-1 mt-1">
          <span className="text-[var(--text-muted)]">
            {lang === "es" ? "Margen por ración" : "Margin per portion"}
          </span>
          <span
            className={`font-mono-num ${currentMarginPct != null && currentMarginPct < 0 ? "text-[var(--status-negative)]" : ""}`}
            data-testid="dish-current-margin"
          >
            {fmtPrice(currentMarginAbs)}
            {currentMarginPct != null && ` · ${currentMarginPct.toFixed(1)}%`}
          </span>
        </div>
      )}
      {suggested != null && suggested > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-[var(--text-muted)]">
            {lang === "es"
              ? `PVP sugerido (${target.toFixed(0)}% margen)`
              : `Suggested PVP (${target.toFixed(0)}% margin)`}
          </span>
          <span className="font-mono-num text-[var(--primary)]" data-testid="dish-suggested-pvp">
            {fmtPrice(suggested)}
          </span>
        </div>
      )}
    </div>
  );
}

